/**
 * Predefined plan templates.
 *
 * STANDARD_TEMPLATE is the behavioral equivalent of the old hardcoded engine.
 * The full PLAN_TEMPLATES catalog is user-selectable via activePlanTemplateId in prefs.
 */

import { MVP_NODES } from "./item-library.js";
import { type PlanConfig, type PlanTemplate } from "./plan-template.js";
import { strengthenStop } from "./stop-builders/strengthen.js";
import { reviewStop } from "./stop-builders/review.js";
import { introduceStop } from "./stop-builders/introduce.js";
import { maintenanceStop } from "./stop-builders/maintenance.js";

// ─── configs ──────────────────────────────────────────────────────────────────

export const STANDARD_CONFIG: PlanConfig = {
  warmupItemCap: 6,
  newItemCap: 3,
  warmupLapseThreshold: 0.3,
  warmupLapseMinCount: 3,
  relistenLapseThreshold: 0.4,
  lapseWindowMs: 7 * 24 * 60 * 60 * 1000,
  reviewBudgetFraction: 0.5,
  weakSpotMinReps: 10,
  minNodeEngagement: 3,
  minPracticeReviewsPerNode: 3,
  maintenanceItemCap: 8,
  reviewLookAheadMs: 2 * 24 * 60 * 60 * 1000, // pull items due within 48 h
  interventionLapseThreshold: 0.4,
  interventionMinAttempts: 15,
};

// ─── catalog ──────────────────────────────────────────────────────────────────

export const PLAN_TEMPLATES: readonly PlanTemplate[] = [
  {
    id: "standard",
    label: "Standard",
    description: "Balanced daily practice across all tracks.",
    nodes: MVP_NODES,
    stops: [strengthenStop, reviewStop, introduceStop, maintenanceStop],
    config: STANDARD_CONFIG,
  },
  {
    id: "intensive",
    label: "Intensive",
    description: "More items per session, tighter lapse detection.",
    nodes: MVP_NODES,
    stops: [strengthenStop, reviewStop, introduceStop, maintenanceStop],
    config: {
      ...STANDARD_CONFIG,
      warmupItemCap: 10,
      newItemCap: 6,
      warmupLapseThreshold: 0.25,
    },
  },
  {
    id: "review-sprint",
    label: "Review Sprint",
    description: "Strengthen and review only — no new material. Good for exam prep.",
    nodes: MVP_NODES,
    stops: [strengthenStop, reviewStop, maintenanceStop],
    config: { ...STANDARD_CONFIG, reviewBudgetFraction: 0.9 },
  },
  {
    id: "harmony-focus",
    label: "Harmony Focus",
    description: "Restricts practice to harmony and interval tracks.",
    nodes: MVP_NODES.filter((n) => ["harmony", "intervals"].includes(n.track)),
    stops: [strengthenStop, reviewStop, introduceStop, maintenanceStop],
    config: STANDARD_CONFIG,
  },
  {
    id: "pitch-and-modes",
    label: "Pitch & Modes",
    description: "Restricts practice to pitch and modes tracks.",
    nodes: MVP_NODES.filter((n) => ["pitch", "modes"].includes(n.track)),
    stops: [strengthenStop, reviewStop, introduceStop, maintenanceStop],
    config: STANDARD_CONFIG,
  },
];

export const STANDARD_TEMPLATE = PLAN_TEMPLATES[0]!;

export const VALID_PLAN_TEMPLATE_IDS = new Set(PLAN_TEMPLATES.map((t) => t.id));

export function getPlanTemplate(id: string): PlanTemplate {
  return PLAN_TEMPLATES.find((t) => t.id === id) ?? STANDARD_TEMPLATE;
}
